import { createContext, useCallback, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import { isTokenDue, jwtExpiresAt } from "@dolfin/lib";
import { SESSION_STORAGE_KEY, ACTIVE_CLIENT_KEY } from "@/lib/constants";
import {
  readStoredSession,
  sessionRefresher,
  userFromAuthResponse,
  type AuthApiResponse,
  type DevPersona,
  type Session,
  type User,
  type UserClient,
} from "./session";

export type { DevPersona } from "./session";

export type OAuthProvider = "google" | "email";

interface AuthContextType {
  user: User | null;
  token: string | null;
  activeClientId: string | null;
  activeClient: UserClient | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (accessToken: string, provider?: OAuthProvider) => Promise<{ error?: string }>;
  /** Local development only: sign in as a seeded manual-test persona. The backend refuses it everywhere else. */
  devLogin: (persona: DevPersona) => Promise<{ error?: string }>;
  /** The dev-login persona this session belongs to, or null for a real sign-in. */
  devPersona: DevPersona | null;
  logout: () => void;
  switchClient: (clientId: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [activeClientId, setActiveClientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [devPersona, setDevPersona] = useState<DevPersona | null>(null);
  const googleAccessTokenRef = useRef<string | null>(null);

  // Mirror the stored session into React state. Runs on mount, whenever this tab's refresher rewrites the
  // session, and when another tab writes it (storage event), so every open tab follows the same session.
  const hydrate = useCallback(() => {
    const session = readStoredSession();
    if (session?.token) {
      setToken(session.token);
      setUser(session.user);
      setActiveClientId(session.activeClientId);
      setDevPersona(session.devPersona ?? null);
    } else {
      setToken(null);
      setUser(null);
      setActiveClientId(null);
      setDevPersona(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // An expired access token no longer means signed out: the refresh cookie may still be good. A dead cookie
      // makes the refresher clear the session, and hydrate() then lands on signed-out.
      const stored = readStoredSession();
      if (stored?.token && isTokenDue(stored.token)) await sessionRefresher.refresh(stored.token);
      if (cancelled) return;
      hydrate();
      setIsLoading(false);
    })();

    const unsubscribe = sessionRefresher.subscribe(hydrate);
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_STORAGE_KEY) hydrate();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener("storage", onStorage);
    };
  }, [hydrate]);

  // Renew a minute before the access token expires, so an open tab never hits the 401 path at all.
  useEffect(() => {
    if (!token) return;
    const exp = jwtExpiresAt(token);
    if (exp === null) return;
    const timer = setTimeout(() => void sessionRefresher.refresh(token), Math.max(exp - 60_000 - Date.now(), 0));
    return () => clearTimeout(timer);
  }, [token]);

  const saveSession = (authToken: string, authUser: User, clientId: string, persona: DevPersona | null = null) => {
    setToken(authToken);
    setUser(authUser);
    setActiveClientId(clientId);
    setDevPersona(persona);
    const session: Session = { token: authToken, user: authUser, activeClientId: clientId, devPersona: persona };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    if (clientId) localStorage.setItem(ACTIVE_CLIENT_KEY, clientId);
  };

  const login = async (accessToken: string, provider: OAuthProvider = "google"): Promise<{ error?: string }> => {
    const resp = await fetch("/api/proxy/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, provider }),
    });

    if (!resp.ok) {
      const problem = await resp.json().catch(() => null);
      if (resp.status === 404) {
        return { error: "You don't have a portal account yet. Contact hello@dolfinai.co for access." };
      }
      return { error: problem?.detail ?? "Login failed. Please try again." };
    }

    const data: AuthApiResponse = await resp.json();
    if (provider === "google") googleAccessTokenRef.current = accessToken;
    return startSession(data);
  };

  // Dev login returns the same session shape as Google login, so it lands in the same startSession path.
  const devLogin = async (persona: DevPersona): Promise<{ error?: string }> => {
    const resp = await fetch("/api/proxy/v1/auth/dev-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ personaKey: persona.key }),
    });

    if (!resp.ok) {
      const problem = await resp.json().catch(() => null);
      return { error: problem?.detail ?? "Dev login failed." };
    }

    return startSession(await resp.json(), persona);
  };

  const startSession = (data: AuthApiResponse, persona: DevPersona | null = null): { error?: string } => {
    const authUser = userFromAuthResponse(data);

    if (authUser.clients.length === 0) {
      return { error: "You're signed in but have no Client memberships. Contact your administrator to be invited." };
    }

    // Restore last-active client if still valid; otherwise default to first
    const remembered = localStorage.getItem(ACTIVE_CLIENT_KEY);
    const defaultClientId = (remembered && authUser.clients.some((c) => c.clientId === remembered))
      ? remembered
      : authUser.clients[0]!.clientId;

    saveSession(data.token, authUser, defaultClientId, persona);
    return {};
  };

  const switchClient = (clientId: string) => {
    if (!token || !user) return;
    if (!user.clients.some((c) => c.clientId === clientId)) return;
    setActiveClientId(clientId);
    localStorage.setItem(ACTIVE_CLIENT_KEY, clientId);
    const session: Session = { token, user, activeClientId: clientId, devPersona };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  };

  const logout = () => {
    void sessionRefresher.logout();
    if (googleAccessTokenRef.current) {
      const g = (window as unknown as { google?: { accounts?: { oauth2?: { revoke?: (t: string) => void } } } }).google;
      g?.accounts?.oauth2?.revoke?.(googleAccessTokenRef.current);
      googleAccessTokenRef.current = null;
    }
    setToken(null);
    setUser(null);
    setActiveClientId(null);
    setDevPersona(null);
    localStorage.removeItem(SESSION_STORAGE_KEY);
  };

  const activeClient = user && activeClientId
    ? user.clients.find((c) => c.clientId === activeClientId) ?? null
    : null;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      activeClientId,
      activeClient,
      isAuthenticated: !!token,
      isLoading,
      login,
      devLogin,
      devPersona,
      logout,
      switchClient,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
